import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Box, Column, Row, Text } from '~/commonComponents';
import {
  CandidateStatus,
  CandidateStatusMapper,
  ICandidateListViewEntity,
  useToggleShortlistCandidate,
} from '~/domain/PermanentJobs';
import DefaultProfileImage from '~/images/doctor.png';
import {
  ArrowRightCircleOutlineIcon,
  BookmarkOutlineIcon,
  BookmarkSolidIcon,
  ChatOutlineIcon,
  CheckCircleOutlineIcon,
  CloseCircleOutlineIcon,
} from '~/images/icons';
import { Colors } from '~/themes/colors';
import './ListItem.style.scss';

interface IListItemProps {
  data: ICandidateListViewEntity;
  onHireClick?: (candidate: Pick<ICandidateListViewEntity, 'id' | 'professional'>) => void;
  onRejectClick?: (candidate: Pick<ICandidateListViewEntity, 'id' | 'professional'>) => void;
}

export const ListItem = ({
  data: {
    createdAt,
    id,
    isShortlisted,
    professional,
    status,
    onsiteInterviewId,
    yearsOfExperience,
  },
  onHireClick,
  onRejectClick,
}: IListItemProps) => {
  const navigate = useNavigate();

  const { mutate: doShortlistCandidate, isPending: isShortlistCandidatePending } =
    useToggleShortlistCandidate({
      candidateId: id,
      isShortlisted,
    });

  const isHired = status === CandidateStatus.Hired;
  const isInterviewed = status === CandidateStatus.Interviewed;
  const isRejected = status === CandidateStatus.Rejected;
  const isScheduled = status === CandidateStatus.Scheduled;

  const StatusActions = useMemo(() => {
    if (isHired) {
      return (
        <Text bold fontSize={18} marginLeft={40}>
          Hired
        </Text>
      );
    }

    if (isRejected) {
      return (
        <Text bold fontSize={18} marginLeft={40}>
          Rejected
        </Text>
      );
    }

    if (isScheduled) {
      return (
        <button
          className="btn-message"
          onClick={() => navigate(`/dashboard/chat/${onsiteInterviewId}`)}
        >
          <ChatOutlineIcon color={Colors.secondary_500} />
          <Text bold fontSize={18}>
            Message
          </Text>
        </button>
      );
    }

    return (
      <>
        <button className="btn-reject" onClick={() => onRejectClick?.({ id, professional })}>
          <CloseCircleOutlineIcon color={Colors.warning_500} />
          <Text bold fontSize={18}>
            Reject
          </Text>
        </button>
        {isInterviewed ? (
          <button className="btn-hire" onClick={() => onHireClick?.({ id, professional })}>
            <CheckCircleOutlineIcon />
            <Text bold fontSize={18}>
              Hire
            </Text>
          </button>
        ) : (
          <button
            className="btn-review"
            onClick={() => navigate(`/dashboard/job/${onsiteInterviewId}/confirm/${id}`)}
          >
            <ArrowRightCircleOutlineIcon color={Colors.secondary_500} />
            <Text bold fontSize={18}>
              Review
            </Text>
          </button>
        )}
      </>
    );
  }, [
    isHired,
    isRejected,
    isScheduled,
    isInterviewed,
    navigate,
    onsiteInterviewId,
    onRejectClick,
    id,
    professional,
    onHireClick,
  ]);

  return (
    <Row
      alignItems="center"
      backgroundColor={Colors.white}
      borderRadius={10}
      boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.15), 0 1px 3px 1px rgba(0, 0, 0, 0.15)"
      display="grid"
      gridTemplateColumns="96px 1fr 15% 12% 25%"
      id="pj-candidate-list-item"
      key={id}
      paddingBlock={12}
    >
      <Box
        alignItems="center"
        backgroundColor="#DFEBED"
        borderRadius="100%"
        height={48}
        justifyContent="center"
        justifySelf="flex-end"
        marginRight={16}
        width={48}
      >
        {professional.picture ? (
          <img
            src={professional.picture}
            height="100%"
            width="100%"
            style={{
              borderRadius: 100,
              objectFit: 'cover',
            }}
          />
        ) : (
          <img src={DefaultProfileImage} height={36} width={36} />
        )}
      </Box>
      <Column>
        <Text
          bold
          fontSize={18}
          style={{ cursor: 'pointer' }}
          textDecoration="underline"
          onClick={() => navigate(`/dashboard/professional/${professional.id}`)}
        >
          {professional.name}
        </Text>
        <Text color={Colors.neutral_500}>
          <Text bold color={Colors.neutral_500}>
            Experience
          </Text>
          : {yearsOfExperience} years
        </Text>
        <Text color={Colors.neutral_500}>
          <Text bold color={Colors.neutral_500}>
            Applied on
          </Text>
          : {createdAt}
        </Text>
      </Column>
      <Text color={Colors.neutral_500}>{CandidateStatusMapper.toProcessStep(status)}</Text>
      <button
        disabled={isRejected || isShortlistCandidatePending}
        style={{
          background: 'transparent',
        }}
        onClick={() => doShortlistCandidate()}
      >
        {isShortlisted ? (
          <BookmarkSolidIcon color={isRejected ? Colors.secondary_200 : Colors.secondary_500} />
        ) : (
          <BookmarkOutlineIcon color={isRejected ? Colors.secondary_200 : Colors.secondary_500} />
        )}
      </button>
      <Row gap={12}>{StatusActions}</Row>
    </Row>
  );
};
